(() => {
  'use strict';

  const SELECTORS = {
    component: '[data-wbu-size-advice]',
    modal: '[data-wbu-size-modal]',
    openButton: '[data-wbu-size-modal-open]',
    closeButton: '[data-wbu-size-modal-close]',
    dialog: '.wbu-size-modal__dialog',

    productForm: 'form[action*="/cart/add"]',
    variantInput: '[name="id"]',

    variantsJson: '[data-wbu-variants-json]',

    selectedVariant: '[data-wbu-selected-variant]',
    visibleVariant: '[data-wbu-visible-variant]',
    visibleVariantValue: '[data-wbu-visible-variant-value]',

    firstName: '#WbuSizeFirstName',
    lastName: '#WbuSizeLastName',

    requestReference: '[data-wbu-request-reference]',

    form: '#WbuSizeAdviceForm',

    submitButton: '[data-wbu-size-submit]',
    submitLabel: '[data-wbu-submit-label]',
    submitLoading: '[data-wbu-submit-loading]',

    success: '[data-wbu-form-success]',
    errors: '[data-wbu-form-errors]'
  };


  class WbuSizeAdvice {

    constructor(component) {

      this.component = component;

      this.variants = this.getVariants();

      this.modal = component.querySelector(
        SELECTORS.modal
      );


      /*
       * Move modal directly into body to avoid
       * stacking context / overflow problems
       * created by Shopify theme sections.
       */
      if (
        this.modal &&
        this.modal.parentElement !== document.body
      ) {
        document.body.appendChild(
          this.modal
        );
      }


      this.dialog = this.modal
        ? this.modal.querySelector(
            SELECTORS.dialog
          )
        : null;


      this.openButton =
        component.querySelector(
          SELECTORS.openButton
        );


      this.closeButtons = this.modal
        ? this.modal.querySelectorAll(
            SELECTORS.closeButton
          )
        : [];


      this.form = this.modal
        ? this.modal.querySelector(
            SELECTORS.form
          )
        : null;


      this.previouslyFocusedElement = null;

      this.isOpen = false;


      if (
        !this.modal ||
        !this.dialog ||
        !this.openButton
      ) {
        return;
      }


      this.handleKeydown =
        this.handleKeydown.bind(this);

      this.handleVariantChange =
        this.handleVariantChange.bind(this);


      this.bindEvents();

      this.updateSelectedVariant();

      this.handleFormResponse();
    }



    /*
     * ========================================================
     * PRODUCT VARIANTS
     * ========================================================
     */

    getVariants() {

      const jsonElement =
        this.component.querySelector(
          SELECTORS.variantsJson
        );


      if (!jsonElement) {
        return [];
      }


      try {

        const parsed =
          JSON.parse(
            jsonElement.textContent
          );


        return Array.isArray(parsed)
          ? parsed
          : [];

      } catch (error) {

        console.warn(
          'WBU Size Advice: unable to read product variants.',
          error
        );

        return [];
      }
    }



    getProductForm() {

      const forms =
        Array.from(
          document.querySelectorAll(
            SELECTORS.productForm
          )
        );


      if (!forms.length) {
        return null;
      }


      const formWithVariant =
        forms.find((form) => {

          const variantInput =
            form.querySelector(
              SELECTORS.variantInput
            );


          return (
            variantInput &&
            variantInput.value
          );
        });


      return (
        formWithVariant ||
        forms[0] ||
        null
      );
    }



    getSelectedVariantId() {

      const productForm =
        this.getProductForm();


      if (productForm) {

        const variantInput =
          productForm.querySelector(
            SELECTORS.variantInput
          );


        if (
          variantInput &&
          variantInput.value
        ) {

          return String(
            variantInput.value
          );

        }

      }


      return String(
        this.component.dataset.initialVariantId ||
        ''
      );
    }



    getSelectedVariant() {

      const selectedId =
        this.getSelectedVariantId();


      if (!selectedId) {
        return null;
      }


      return (
        this.variants.find((variant) => {

          return (
            String(variant.id) ===
            selectedId
          );

        }) ||
        null
      );
    }



    /*
     * Return customer-facing variant information.
     *
     * Example:
     * Shopify variant ID: 54331355955529
     * Customer size: 54
     *
     * Output:
     * 54
     */
    getSelectedVariantText() {

      const variant =
        this.getSelectedVariant();


      if (variant) {

        let options = [];


        if (
          Array.isArray(
            variant.options
          )
        ) {

          options =
            variant.options
              .map((option) =>
                String(option).trim()
              )
              .filter((option) => {

                return (
                  option &&
                  option.toLowerCase() !==
                    'default title'
                );

              });

        }


        if (options.length) {

          return options.join(' / ');

        }


        if (
          variant.title &&
          variant.title.toLowerCase() !==
            'default title'
        ) {

          return variant.title.trim();

        }

      }


      /*
       * Never expose Shopify Variant ID
       * to the customer.
       */
      return '';
    }



    /*
     * ========================================================
     * TEMPORARY SUBMITTED SIZE STORAGE
     *
     * Shopify reloads the product page after the contact form
     * submission. This would normally reset the product to its
     * default variant.
     *
     * We therefore store the customer's selected size in
     * sessionStorage before submission and restore it in the
     * success modal.
     * ========================================================
     */

    getSizeStorageKey() {

      const productTitle =
        this.component.dataset.productTitle ||
        window.location.pathname;


      return (
        'wbu-size-help-submitted-size-' +
        productTitle
      );
    }



    storeSubmittedSize() {

      const selectedSize =
        this.getSelectedVariantText();


      if (!selectedSize) {
        return;
      }


      try {

        sessionStorage.setItem(
          this.getSizeStorageKey(),
          selectedSize
        );

      } catch (error) {

        console.warn(
          'WBU Size Advice: unable to store submitted size.',
          error
        );

      }
    }



    getStoredSubmittedSize() {

      try {

        return (
          sessionStorage.getItem(
            this.getSizeStorageKey()
          ) ||
          ''
        );

      } catch (error) {

        return '';

      }
    }



    /*
     * ========================================================
     * EVENTS
     * ========================================================
     */

    bindEvents() {

      this.openButton.addEventListener(
        'click',
        () => {

          this.open();

        }
      );


      this.closeButtons.forEach(
        (button) => {

          button.addEventListener(
            'click',
            () => {

              this.close();

            }
          );

        }
      );


      document.addEventListener(
        'change',
        this.handleVariantChange
      );


      /*
       * Additional safeguard for Envy variant selectors.
       */
      document.addEventListener(
        'click',
        (event) => {

          const target =
            event.target.closest(
              'label, button, input, select'
            );


          if (!target) {
            return;
          }


          window.setTimeout(
            () => {

              this.updateSelectedVariant();

            },
            80
          );

        }
      );


      if (this.form) {

        this.form.addEventListener(
          'submit',
          () => {

            /*
             * Refresh selected size immediately
             * before form submission.
             */
            this.updateSelectedVariant();


            /*
             * IMPORTANT:
             * Remember the selected size before Shopify
             * reloads the page.
             */
            this.storeSubmittedSize();


            this.prepareRequestReference();

            this.setSubmittingState();

          }
        );


        const firstName =
          this.form.querySelector(
            SELECTORS.firstName
          );


        const lastName =
          this.form.querySelector(
            SELECTORS.lastName
          );


        [
          firstName,
          lastName
        ].forEach((field) => {

          if (!field) {
            return;
          }


          field.addEventListener(
            'input',
            () => {

              this.prepareRequestReference();

            }
          );

        });

      }

    }



    /*
     * ========================================================
     * MODAL OPEN / CLOSE
     * ========================================================
     */

    open() {

      if (this.isOpen) {
        return;
      }


      this.previouslyFocusedElement =
        document.activeElement;


      this.isOpen = true;


      this.updateSelectedVariant();


      this.modal.classList.add(
        'is-open'
      );


      this.modal.setAttribute(
        'aria-hidden',
        'false'
      );


      document.documentElement
        .classList.add(
          'wbu-modal-is-open'
        );


      document.body
        .classList.add(
          'wbu-modal-is-open'
        );


      document.addEventListener(
        'keydown',
        this.handleKeydown
      );


      window.requestAnimationFrame(
        () => {

          this.dialog.focus();

        }
      );
    }



    close() {

      if (!this.isOpen) {
        return;
      }


      this.isOpen = false;


      this.modal.classList.remove(
        'is-open'
      );


      this.modal.setAttribute(
        'aria-hidden',
        'true'
      );


      document.documentElement
        .classList.remove(
          'wbu-modal-is-open'
        );


      document.body
        .classList.remove(
          'wbu-modal-is-open'
        );


      document.removeEventListener(
        'keydown',
        this.handleKeydown
      );


      if (
        this.previouslyFocusedElement &&
        typeof
          this.previouslyFocusedElement
            .focus === 'function'
      ) {

        this.previouslyFocusedElement
          .focus();

      }

    }



    /*
     * ========================================================
     * ACCESSIBILITY
     * ========================================================
     */

    handleKeydown(event) {

      if (event.key === 'Escape') {

        this.close();

        return;

      }


      if (event.key !== 'Tab') {
        return;
      }


      const focusableElements =
        this.dialog.querySelectorAll(
          [
            'a[href]',
            'button:not([disabled])',
            'input:not([disabled]):not([type="hidden"])',
            'textarea:not([disabled])',
            'select:not([disabled])',
            '[tabindex]:not([tabindex="-1"])'
          ].join(',')
        );


      if (!focusableElements.length) {

        event.preventDefault();

        this.dialog.focus();

        return;

      }


      const firstElement =
        focusableElements[0];


      const lastElement =
        focusableElements[
          focusableElements.length - 1
        ];


      if (
        event.shiftKey &&
        document.activeElement ===
          firstElement
      ) {

        event.preventDefault();

        lastElement.focus();

      } else if (
        !event.shiftKey &&
        document.activeElement ===
          lastElement
      ) {

        event.preventDefault();

        firstElement.focus();

      }

    }



    /*
     * ========================================================
     * VARIANT CHANGE
     * ========================================================
     */

    handleVariantChange() {

      window.requestAnimationFrame(
        () => {

          window.setTimeout(
            () => {

              this.updateSelectedVariant();

            },
            40
          );

        }
      );
    }



    /*
     * ========================================================
     * DISPLAY SELECTED SIZE
     * ========================================================
     */

    updateSelectedVariant(
      forcedSize = ''
    ) {

      const selectedVariantText =
        forcedSize ||
        this.getSelectedVariantText();


      const hiddenVariant =
        this.modal.querySelector(
          SELECTORS.selectedVariant
        );


      const visibleVariant =
        this.modal.querySelector(
          SELECTORS.visibleVariant
        );


      const visibleVariantValue =
        this.modal.querySelector(
          SELECTORS.visibleVariantValue
        );


      if (hiddenVariant) {

        hiddenVariant.value =
          selectedVariantText;

      }


      if (visibleVariantValue) {

        visibleVariantValue.textContent =
          selectedVariantText;

      }


      if (visibleVariant) {

        visibleVariant.hidden =
          !selectedVariantText;

      }

    }



    /*
     * ========================================================
     * REQUEST REFERENCE
     * ========================================================
     */

    prepareRequestReference() {

      if (!this.form) {
        return;
      }


      const firstName =
        this.form.querySelector(
          SELECTORS.firstName
        );


      const lastName =
        this.form.querySelector(
          SELECTORS.lastName
        );


      const reference =
        this.form.querySelector(
          SELECTORS.requestReference
        );


      if (!reference) {
        return;
      }


      const customerName = [

        firstName
          ? firstName.value.trim()
          : '',

        lastName
          ? lastName.value.trim()
          : ''

      ]
        .filter(Boolean)
        .join(' ');


      reference.value =
        customerName
          ? `WBU Size Help - ${customerName}`
          : 'WBU Size Help';

    }



    /*
     * ========================================================
     * SUBMIT STATE
     * ========================================================
     */

    setSubmittingState() {

      if (!this.form) {
        return;
      }


      const submitButton =
        this.form.querySelector(
          SELECTORS.submitButton
        );


      const submitLabel =
        this.form.querySelector(
          SELECTORS.submitLabel
        );


      const submitLoading =
        this.form.querySelector(
          SELECTORS.submitLoading
        );


      if (!submitButton) {
        return;
      }


      submitButton.disabled = true;


      submitButton.setAttribute(
        'aria-busy',
        'true'
      );


      if (submitLabel) {

        submitLabel.hidden = true;

      }


      if (submitLoading) {

        submitLoading.hidden = false;

      }

    }



    /*
     * ========================================================
     * SHOPIFY FORM RESPONSE
     * ========================================================
     */

    handleFormResponse() {

      const success =
        this.modal.querySelector(
          SELECTORS.success
        );


      const errors =
        this.modal.querySelector(
          SELECTORS.errors
        );


      if (
        !success &&
        !errors
      ) {
        return;
      }


      /*
       * Shopify has reloaded the page.
       * The product selector may now show its default size,
       * so retrieve the size the customer actually submitted.
       */
      const submittedSize =
        this.getStoredSubmittedSize();


      this.open();


      /*
       * Override the default variant displayed after reload
       * with the actual submitted size.
       */
      if (
        success &&
        submittedSize
      ) {

        this.updateSelectedVariant(
          submittedSize
        );

      }


      window.requestAnimationFrame(
        () => {

          const responseMessage =
            success || errors;


          if (responseMessage) {

            responseMessage.focus();

          }

        }
      );

    }

  }



  /*
   * ==========================================================
   * INITIALISATION
   * ==========================================================
   */

  const initialise = () => {

    document
      .querySelectorAll(
        SELECTORS.component
      )
      .forEach(
        (component) => {

          if (
            component.dataset
              .wbuInitialised ===
            'true'
          ) {
            return;
          }


          component.dataset
            .wbuInitialised =
            'true';


          new WbuSizeAdvice(
            component
          );

        }
      );

  };



  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      initialise
    );

  } else {

    initialise();

  }



  /*
   * Shopify Theme Editor support.
   */

  document.addEventListener(
    'shopify:section:load',
    initialise
  );

})();